import { useCallback, useEffect, useRef, useState } from 'react'
import { CsvColumnConfig, CsvColumnValidation, CsvProfile, CsvUxState, CsvWorkerRequest, CsvWorkerResponse } from '../types/csv.types'
import { analyzeCsv, processCsv, validateConfiguration } from '../services/csvAnalyzer'
import { createCsvWorker } from './createCsvWorker'

const DEFAULT_PREVIEW_ROWS = 100
const DEFAULT_SAMPLE_ROWS = 5000

type ProgressPayload = { processedRows: number; progress: number; stage?: string }

interface UseCsvAnalyzerReturn {
    state: CsvUxState
    progress: ProgressPayload | null
    profile: CsvProfile | null
    validations: CsvColumnValidation[]
    error: string | null
    analyze: (file: File) => Promise<CsvProfile>
    validate: (configs: CsvColumnConfig[], sampleRows: Record<string, string>[]) => Promise<CsvColumnValidation[]>
    process: (file: File, delimiter: string, configs: CsvColumnConfig[], outputFormat: 'csv' | 'json', encoding?: string) => Promise<{ blob: Blob; totalRows: number; fileName: string }>
    reset: () => void
}

function tryCreateWorker(): Worker | null {
    try {
        return createCsvWorker()
    } catch {
        return null
    }
}

interface PendingRequest {
    onProgress?: (p: ProgressPayload) => void
    resolve: (value: CsvWorkerResponse) => void
    reject: (err: Error) => void
}

export function useCsvAnalyzer(): UseCsvAnalyzerReturn {
    const [state, setState] = useState<CsvUxState>('idle')
    const [progress, setProgress] = useState<ProgressPayload | null>(null)
    const [profile, setProfile] = useState<CsvProfile | null>(null)
    const [validations, setValidations] = useState<CsvColumnValidation[]>([])
    const [error, setError] = useState<string | null>(null)
    const workerRef = useRef<Worker | null>(null)
    const pendingRef = useRef<PendingRequest | null>(null)

    useEffect(() => {
        return () => {
            workerRef.current?.terminate()
            workerRef.current = null
        }
    }, [])

    const sendToWorker = useCallback((request: CsvWorkerRequest, onProgress?: (p: ProgressPayload) => void): Promise<CsvWorkerResponse> => {
        return new Promise((resolve, reject) => {
            workerRef.current ??= tryCreateWorker()
            const worker = workerRef.current
            if (!worker) {
                reject(new Error('Worker unavailable'))
                return
            }
            pendingRef.current = { onProgress, resolve, reject }

            const cleanup = () => {
                worker.removeEventListener('message', onMessage)
                worker.removeEventListener('error', onError)
            }

            const onMessage = (event: MessageEvent<CsvWorkerResponse>) => {
                const msg = event.data
                if (msg.kind === 'ANALYSIS_PROGRESS') {
                    onProgress?.({ processedRows: msg.processedRows, progress: msg.progress, stage: msg.stage })
                    return
                }
                if (msg.kind === 'PROCESS_PROGRESS') {
                    onProgress?.({ processedRows: msg.processedRows, progress: msg.progress })
                    return
                }
                cleanup()
                if (msg.kind === 'ERROR') reject(new Error(msg.message))
                else resolve(msg)
            }
            const onError = (event: ErrorEvent) => {
                cleanup()
                reject(new Error(event.message || 'Worker error'))
            }

            worker.addEventListener('message', onMessage)
            worker.addEventListener('error', onError)
            worker.postMessage(request)
        })
    }, [])

    const analyze = useCallback(
        async (file: File): Promise<CsvProfile> => {
            setState('analyzing')
            setError(null)
            setProgress({ processedRows: 0, progress: 0, stage: 'starting' })
            const request: CsvWorkerRequest = {
                kind: 'ANALYZE_FILE',
                file,
                maxPreviewRows: DEFAULT_PREVIEW_ROWS,
                maxSampleRows: DEFAULT_SAMPLE_ROWS,
            }

            try {
                let profileResult: CsvProfile
                try {
                    const response = await sendToWorker(request, (p) => setProgress(p))
                    if (response.kind !== 'ANALYSIS_COMPLETE') throw new Error('Unexpected worker response')
                    profileResult = response.profile
                } catch {
                    // Worker path failed (unavailable or errored); fall back to main-thread analysis.
                    profileResult = await analyzeCsv(file, {
                        maxPreviewRows: DEFAULT_PREVIEW_ROWS,
                        maxSampleRows: DEFAULT_SAMPLE_ROWS,
                        onProgress: (p) => setProgress(p),
                    })
                }
                setProfile(profileResult)
                setState('configuring')
                return profileResult
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Analysis failed'
                setError(message)
                setState('error')
                throw err
            }
        },
        [sendToWorker],
    )

    const validate = useCallback(
        async (configs: CsvColumnConfig[], sampleRows: Record<string, string>[]): Promise<CsvColumnValidation[]> => {
            setState('validating')
            setError(null)
            try {
                let result: CsvColumnValidation[]
                try {
                    const response = await sendToWorker({ kind: 'VALIDATE_CONFIGURATION', columnConfigs: configs, sampleRows })
                    if (response.kind !== 'VALIDATION_COMPLETE') throw new Error('Unexpected worker response')
                    result = response.validations
                } catch {
                    result = validateConfiguration(configs, sampleRows)
                }
                setValidations(result)
                setState('ready')
                return result
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Validation failed'
                setError(message)
                setState('error')
                throw err
            }
        },
        [sendToWorker],
    )

    const process = useCallback(
        async (file: File, delimiter: string, configs: CsvColumnConfig[], outputFormat: 'csv' | 'json', encoding?: string) => {
            setState('processing')
            setError(null)
            setProgress({ processedRows: 0, progress: 0 })
            try {
                let blob: Blob
                let totalRows: number
                try {
                    const response = await sendToWorker({ kind: 'PROCESS_FILE', file, delimiter, encoding, columnConfigs: configs, outputFormat }, (p) => setProgress(p))
                    if (response.kind !== 'PROCESS_COMPLETE') throw new Error('Unexpected worker response')
                    blob = response.blob
                    totalRows = response.totalRows
                } catch {
                    const result = await processCsv(file, {
                        delimiter,
                        encoding,
                        columnConfigs: configs,
                        outputFormat,
                        onProgress: (p) => setProgress(p),
                    })
                    blob = result.blob
                    totalRows = result.totalRows
                }
                setState('complete')
                return { blob, totalRows, fileName: file.name }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Processing failed'
                setError(message)
                setState('error')
                throw err
            }
        },
        [sendToWorker],
    )

    const reset = useCallback(() => {
        setState('idle')
        setProgress(null)
        setProfile(null)
        setValidations([])
        setError(null)
    }, [])

    return { state, progress, profile, validations, error, analyze, validate, process, reset }
}
